import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import { apiGet, apiPostPut, apiDelete } from "../../api/api_methods";

const REPORT_PLAN_TYPES = [
  { value: "zeroToOne", label: "Zero2One" },
  { value: "basic", label: "Basic" },
  { value: "comprehensive", label: "Comprehensive" },
  { value: "wealthManagement", label: "Wealth" },
];

// Keys aligned with main Awaken report sections (high-level only)
// Get report section fields based on plan type
const getReportSectionFields = (planType) => {
  if (planType === "zeroToOne") {
    // Zero2One: Only 6 fields in specific order
    return [
      { key: "welcome", label: "Welcome" },
      { key: "introduction", label: "Introduction" },
      { key: "awareness", label: "Awareness" },
      { key: "wings", label: "Wings" },
      { key: "alertness", label: "Alertness" },
      { key: "executePlan", label: "Execution with Precision" },
      { key: "investNow", label: "Invest in the NOW" },
    ];
  } else {
    // Other plans: All fields with wings as 3rd and alertness as 4th
    return [
      { key: "welcome", label: "Welcome" },
      { key: "introduction", label: "Introduction" },
      { key: "awareness", label: "Awareness" },
      { key: "wings", label: "Wings" },
      { key: "alertness", label: "Alertness" },
      { key: "knowledge", label: "Knowledge that Matters" },
      { key: "executePlan", label: "Execution with Precision" },
      { key: "investNow", label: "Invest in the NOW" },
      { key: "disclaimer", label: "Disclaimers & Disclosures" },
    ];
  }
};

const DEFAULT_SECTION_TITLES = {
  welcome: "Welcome",
  introduction: "Introduction",
  awareness: "1. Awareness",
  wings: "2. Wings",
  alertness: "3. Alertness",
  knowledge: "4. Knowledge That Matters",
  executePlan: "5. Execution with precision",
  investNow: "6. Invest in the NOW",
  disclaimer: "DISCLAIMERS & DISCLOSURES",
};

// Toolbar matches Awaken-app `src/utils/reportWriteupRich.js` (report + email rich text).
const EMAIL_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    [{ font: [] }, { size: [] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link"],
    ["clean"],
  ],
};

const EMAIL_EDITOR_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "align",
  "list",
  "bullet",
  "blockquote",
  "code-block",
  "link",
];

export default function TemplatesManager() {
  const [activeTemplateTab, setActiveTemplateTab] = useState("email"); // 'email' | 'report'

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null); // template object or null
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailForm, setEmailForm] = useState({
    name: "",
    description: "",
    subjectTemplate: "",
    bodyTemplateHtml: "",
  });

  // Report templates state
  const [reportTemplates, setReportTemplates] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [editingReport, setEditingReport] = useState(null); // template object or null
  const [showReportForm, setShowReportForm] = useState(false);

  const [reportForm, setReportForm] = useState({
    name: "",
    planType: "basic",
    sectionWriteups: {},
    sectionTitles: {},
  });

  // --- EMAIL TEMPLATES LOGIC ---
  const fetchEmailTemplates = async () => {
    try {
      setLoadingEmail(true);
      const res = await apiGet("/email-templates");
      if (res.status === 200 && res.body && res.body.success) {
        setEmailTemplates(res.body.data || []);
      } else if (Array.isArray(res.body)) {
        // Fallback if backend simply returns array
        setEmailTemplates(res.body);
      }
    } catch (err) {
      console.error("Error fetching email templates:", err);
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleEditEmail = (tpl) => {
    setEditingEmail(tpl);
    setShowEmailForm(true);
    setEmailForm({
      name: tpl.name || "",
      description: tpl.description || "",
      subjectTemplate: tpl.subjectTemplate || "",
      bodyTemplateHtml: tpl.bodyTemplateHtml || "",
    });
  };

  const resetEmailForm = () => {
    setEditingEmail(null);
    setShowEmailForm(false);
    setEmailForm({
      name: "",
      description: "",
      subjectTemplate: "",
      bodyTemplateHtml: "",
    });
  };

  const getEmailBodyPlainText = (html) =>
    String(html || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

  const handleSaveEmail = async () => {
    if (!emailForm.name.trim()) {
      alert("Template name is required");
      return;
    }
    if (!emailForm.subjectTemplate.trim()) {
      alert("Subject template is required");
      return;
    }
    if (!getEmailBodyPlainText(emailForm.bodyTemplateHtml)) {
      alert("Body template (HTML) is required");
      return;
    }

    try {
      setLoadingEmail(true);
      const payload = {
        name: emailForm.name.trim(),
        description: emailForm.description.trim(),
        subjectTemplate: emailForm.subjectTemplate,
        bodyTemplateHtml: emailForm.bodyTemplateHtml,
      };
      let res;
      if (editingEmail && editingEmail.id) {
        res = await apiPostPut(payload, `/email-templates/${editingEmail.id}`, "PUT");
      } else {
        res = await apiPostPut(payload, "/email-templates", "POST");
      }
      if (res.status === 200 || res.status === 201) {
        resetEmailForm();
        fetchEmailTemplates();
      } else {
        alert("Failed to save email template");
      }
    } catch (err) {
      console.error("Error saving email template:", err);
      alert("Error saving email template. Please try again.");
    } finally {
      setLoadingEmail(false);
    }
  };

  const handleDeleteEmail = async (tpl) => {
    if (!tpl || !tpl.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete email template "${tpl.name}"?`)) return;
    try {
      setLoadingEmail(true);
      const res = await apiDelete(`/email-templates/${tpl.id}`);
      if (res.status === 200 || res.status === 204) {
        fetchEmailTemplates();
      } else {
        alert("Failed to delete email template");
      }
    } catch (err) {
      console.error("Error deleting email template:", err);
      alert("Error deleting email template. Please try again.");
    } finally {
      setLoadingEmail(false);
    }
  };

  // --- REPORT TEMPLATES LOGIC ---
  const fetchReportTemplates = async () => {
    try {
      setLoadingReport(true);
      const res = await apiGet("/report-templates");
      if (res.status === 200 && res.body && res.body.success) {
        setReportTemplates(res.body.data || []);
      } else if (Array.isArray(res.body)) {
        setReportTemplates(res.body);
      }
    } catch (err) {
      console.error("Error fetching report templates:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleEditReport = (tpl) => {
    const normalizedSectionWriteups = {
      ...(tpl.sectionWriteups || {}),
    };
    if (
      !normalizedSectionWriteups.disclaimer &&
      normalizedSectionWriteups.conclusion
    ) {
      normalizedSectionWriteups.disclaimer = normalizedSectionWriteups.conclusion;
    }

    setEditingReport(tpl);
    setShowReportForm(true);
    setReportForm({
      name: tpl.name || "",
      planType: tpl.planType || "basic",
      sectionWriteups: normalizedSectionWriteups,
      sectionTitles: tpl.sectionTitles || {},
    });
  };

  const resetReportForm = () => {
    setEditingReport(null);
    setShowReportForm(false);
    setReportForm({
      name: "",
      planType: "basic",
      sectionWriteups: {},
      sectionTitles: {},
    });
  };

  const handleReportFieldChange = (key, value) => {
    setReportForm((prev) => ({
      ...prev,
      sectionWriteups: {
        ...prev.sectionWriteups,
        [key]: value,
      },
    }));
  };

  const handleReportTitleChange = (key, value) => {
    setReportForm((prev) => ({
      ...prev,
      sectionTitles: {
        ...prev.sectionTitles,
        [key]: value,
      },
    }));
  };

  const handleSaveReport = async () => {
    if (!reportForm.name.trim()) {
      alert("Report template name is required");
      return;
    }
    if (!reportForm.planType) {
      alert("Plan type is required");
      return;
    }

    try {
      setLoadingReport(true);
      const payload = {
        name: reportForm.name.trim(),
        planType: reportForm.planType,
        sectionWriteups: {
          ...(reportForm.sectionWriteups || {}),
        },
        sectionTitles: {
          ...(reportForm.sectionTitles || {}),
        },
      };
      // Backward compatibility for consumers still reading "conclusion"
      if (
        !payload.sectionWriteups.conclusion &&
        payload.sectionWriteups.disclaimer
      ) {
        payload.sectionWriteups.conclusion = payload.sectionWriteups.disclaimer;
      }
      let res;
      if (editingReport && editingReport.id) {
        res = await apiPostPut(payload, `/report-templates/${editingReport.id}`, "PUT");
      } else {
        res = await apiPostPut(payload, "/report-templates", "POST");
      }
      if (res.status === 200 || res.status === 201) {
        resetReportForm();
        fetchReportTemplates();
      } else {
        alert("Failed to save report template");
      }
    } catch (err) {
      console.error("Error saving report template:", err);
      alert("Error saving report template. Please try again.");
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDeleteReport = async (tpl) => {
    if (!tpl || !tpl.id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete report template "${tpl.name}"?`)) return;
    try {
      setLoadingReport(true);
      const res = await apiDelete(`/report-templates/${tpl.id}`);
      if (res.status === 200 || res.status === 204) {
        fetchReportTemplates();
      } else {
        alert("Failed to delete report template");
      }
    } catch (err) {
      console.error("Error deleting report template:", err);
      alert("Error deleting report template. Please try again.");
    } finally {
      setLoadingReport(false);
    }
  };

  // Initial loads
  useEffect(() => {
    fetchEmailTemplates();
    fetchReportTemplates();
  }, []);

  return (
    <div className="space-y-8">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" aria-label="Template Tabs">
          <button
            type="button"
            onClick={() => setActiveTemplateTab("email")}
            className={`whitespace-nowrap pb-3 border-b-2 text-sm font-medium ${
              activeTemplateTab === "email"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Email Templates
          </button>
          <button
            type="button"
            onClick={() => setActiveTemplateTab("report")}
            className={`whitespace-nowrap pb-3 border-b-2 text-sm font-medium ${
              activeTemplateTab === "report"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Report Templates
          </button>
        </nav>
      </div>

      {activeTemplateTab === "email" && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Email Templates
            </h2>
            <button
              type="button"
              onClick={() => {
                resetEmailForm();
                setShowEmailForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              New Template
            </button>
          </div>

          {/* Email templates table */}
          <div className="mb-6">
            {loadingEmail ? (
              <div className="py-4 text-gray-500 text-sm">Loading email templates...</div>
            ) : emailTemplates.length === 0 ? (
              <div className="py-4 text-gray-500 text-sm">
                No email templates found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {emailTemplates.map((tpl) => (
                      <tr key={tpl.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {tpl.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {tpl.subjectTemplate}
                        </td>
                        <td className="px-4 py-2 text-right text-sm">
                          <button
                            type="button"
                            onClick={() => handleEditEmail(tpl)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteEmail(tpl)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Email template form - only shown when creating or editing */}
          {showEmailForm && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingEmail
                  ? `Edit Email Template: ${editingEmail.name}`
                  : "Create Email Template"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={emailForm.name}
                    onChange={(e) =>
                      setEmailForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={emailForm.description}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subject Template
                  </label>
                  <input
                    type="text"
                    value={emailForm.subjectTemplate}
                    onChange={(e) =>
                      setEmailForm((prev) => ({
                        ...prev,
                        subjectTemplate: e.target.value,
                      }))
                    }
                    placeholder="e.g. Welcome {{client.name}}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Body Template
                    </label>
                    <span className="text-xs text-gray-500">
                      Placeholders: {"{{client.name}}"}, {"{{retirementAge}}"} / {"{{your_retirement_age}}"},{" "}
                      {"{{overallMonthlyIncome}}"} / {"{{combined_monthly_income}}"},{" "}
                      {"{{goal_1}}"}–{"{{goal_4}}"}, {"{{reportUrl}}"}
                    </span>
                  </div>
                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    <ReactQuill
                      theme="snow"
                      value={emailForm.bodyTemplateHtml}
                      onChange={(value) =>
                        setEmailForm((prev) => ({
                          ...prev,
                          bodyTemplateHtml: value,
                        }))
                      }
                      modules={EMAIL_EDITOR_MODULES}
                      formats={EMAIL_EDITOR_FORMATS}
                      placeholder="Write your email body here..."
                      className="bg-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetEmailForm}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEmail}
                    disabled={loadingEmail}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editingEmail ? "Save Changes" : "Create Template"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {activeTemplateTab === "report" && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Report Templates
            </h2>
            <button
              type="button"
              onClick={() => {
                resetReportForm();
                setShowReportForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              New Template
            </button>
          </div>

          {/* Report templates table */}
          <div className="mb-6">
            {loadingReport ? (
              <div className="py-4 text-gray-500 text-sm">Loading report templates...</div>
            ) : reportTemplates.length === 0 ? (
              <div className="py-4 text-gray-500 text-sm">
                No report templates found.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan Type
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportTemplates.map((tpl) => (
                      <tr key={tpl.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {tpl.name}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {REPORT_PLAN_TYPES.find(pt => pt.value === tpl.planType)?.label || tpl.planType}
                        </td>
                        <td className="px-4 py-2 text-right text-sm">
                          <button
                            type="button"
                            onClick={() => handleEditReport(tpl)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteReport(tpl)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Report template form - only shown when creating or editing */}
          {showReportForm && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {editingReport
                  ? `Edit Report Template: ${editingReport.name}`
                  : "Create Report Template"}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={reportForm.name}
                      onChange={(e) =>
                        setReportForm((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plan Type
                    </label>
                    <select
                      value={reportForm.planType}
                      onChange={(e) => {
                        const newPlanType = e.target.value;
                        const allowedFields = getReportSectionFields(newPlanType).map(f => f.key);
                        
                        // Clean up sectionWriteups to only include fields allowed for the new plan type
                        const cleanedSectionWriteups = {};
                        Object.keys(reportForm.sectionWriteups || {}).forEach(key => {
                          if (allowedFields.includes(key)) {
                            cleanedSectionWriteups[key] = reportForm.sectionWriteups[key];
                          }
                        });
                        
                        setReportForm((prev) => ({
                          ...prev,
                          planType: newPlanType,
                          sectionWriteups: cleanedSectionWriteups,
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {REPORT_PLAN_TYPES.map((pt) => (
                        <option key={pt.value} value={pt.value}>
                          {pt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto border border-gray-100 rounded-md p-3">
                  {getReportSectionFields(reportForm.planType).map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Section Title
                      </label>
                      <input
                        type="text"
                        value={
                          reportForm.sectionTitles?.[field.key] ||
                          DEFAULT_SECTION_TITLES[field.key] ||
                          field.label
                        }
                        onChange={(e) =>
                          handleReportTitleChange(field.key, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
                      />
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Section Description
                        </label>
                        <span className="text-xs text-gray-500">
                          Rich text — same toolbar as email body templates
                        </span>
                      </div>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        <ReactQuill
                          key={`${field.key}-${reportForm.planType}`}
                          theme="snow"
                          value={reportForm.sectionWriteups[field.key] || ""}
                          onChange={(value) =>
                            handleReportFieldChange(field.key, value)
                          }
                          modules={EMAIL_EDITOR_MODULES}
                          formats={EMAIL_EDITOR_FORMATS}
                          placeholder="Section write-up..."
                          className="bg-white text-sm"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetReportForm}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveReport}
                    disabled={loadingReport}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {editingReport ? "Save Changes" : "Create Template"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}


